import { useEffect, useRef, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import avatarImage from "../../../assets/images/avatar.svg";
import birthdayIcon from "../../../assets/images/birthdayicon.svg";
import incidentIcon from "../../../assets/images/incident.svg";
import stageIcon from "../../../assets/icons/stageicon.svg";
import { calculateAge, formatCalendarDate, getCaseId, getClientId } from "../../../Utils/helper";
import { CASES_MERGED_EVENT } from "../../../Utils/caseMergeEvents";
import { fetchCurrentCase, setLastAccessedCases } from "../../../Redux/caseData/caseDataSlice";

import CaseIdentity from "./CaseIdentity";
import ClientDetails from "./ClientDetails";
import MergePreview from "./MergePreview";
import useCaseMerge, { useCaseMergeClients } from "./hooks/useCaseMerge";
import "./case-merge.css";

const CaseOption = ({ caseData, birthday, type, checked, onChange, disabled }) => {
  const age = birthday ? calculateAge(birthday) : "";
  const stage = caseData.auto_case_stage_names?.at(-1);

  return (
    <label
      className={`case-merge-option info-color-panels-odd-even ${checked ? "case-merge-option-selected" : ""}`}
    >
      <input
        type={type}
        name={type === "radio" ? "final-case" : `source-case-${caseData.id}`}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-label={`${type === "radio" ? "Keep" : "Merge and delete"} ${caseData.file_number ? `File #${caseData.file_number}` : `Case #${caseData.id}`}`}
      />
      <span className="case-merge-case-info">
        <span className="case-merge-file-number">
          {caseData.file_number
            ? `File #${caseData.file_number}`
            : `Case #${caseData.id} · No file number`}
        </span>
        <CaseIdentity caseData={caseData}>
          <span className="case-merge-icon-row">
            <img src={birthdayIcon} width="15" height="15" alt="" />
            <span className="visually-hidden">Birthday:</span>
            <span>{formatCalendarDate(birthday) || "Not on file"}</span>
            {Number.isInteger(age) && (
              <>
                <span className="text-primary-25">age</span>
                <span>{age}</span>
              </>
            )}
          </span>
          <span className="case-merge-icon-row">
            <img src={incidentIcon} width="15" height="15" alt="" />
            <span className="visually-hidden">Incident date:</span>
            <span>{formatCalendarDate(caseData.incident_date) || "Not on file"}</span>
          </span>
          {stage && (
            <span className="case-merge-icon-row">
              <img src={stageIcon} width="15" height="15" alt="" />
              <span className="visually-hidden">Stage:</span>
              <span>{stage}</span>
            </span>
          )}
        </CaseIdentity>
      </span>
    </label>
  );
};

const CaseMerge = ({ onBusyChange }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(
    () => location.state?.caseMergeClient || null
  );
  const [showResults, setShowResults] = useState(false);
  const searchInput = useRef(null);
  const clients = useCaseMergeClients(searchTerm);

  const onMerged = async (result, payload) => {
    const count = result.merged_cases?.length ?? payload.source_case_ids.length;
    const file = result.target_case?.file_number;
    toast.success(
      `Merged ${count} ${count === 1 ? "case" : "cases"} into ${file ? `File #${file}` : `Case #${payload.target_case_id}`}`
    );

    // Remove deleted IDs immediately; then refresh the app's recent and pinned lists.
    const deleted = new Set(payload.source_case_ids.map(String));
    const recent = store.getState().caseData?.lastAccessedCases;
    if (Array.isArray(recent))
      dispatch(setLastAccessedCases(recent.filter((item) => !deleted.has(String(item.id)))));
    window.dispatchEvent(new CustomEvent(CASES_MERGED_EVENT, { detail: payload }));

    if (deleted.has(String(getCaseId()))) {
      // Stay in Firm Management when the currently open case was deleted.
      navigate(`/bp-lawfirmdirectory/${payload.client_id}/${payload.target_case_id}`, {
        replace: true,
        state: { firmManagementTab: "case-merge", caseMergeClient: selectedClient },
      });
    } else if (getClientId() && getCaseId()) {
      await dispatch(fetchCurrentCase(getClientId(), getCaseId()));
    }
  };

  const merge = useCaseMerge(selectedClient?.id, onMerged);
  const busy = Boolean(merge.operation);
  const cases = merge.data?.cases || [];

  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);

  const chooseClient = (client) => {
    if (busy) return;
    setSelectedClient(client);
    searchInput.current?.focus();
    setShowResults(false);
  };

  return (
    <div className="case-merge">
      <div className="case-merge-toolbar">
        <p className="case-merge-instructions">
          Select a client, choose the final case to keep, then check the duplicate cases to merge
          and delete. Review the preview before confirming.
        </p>
        <button
          type="button"
          className="btn save-btn-popup popup-heading-color d-flex align-items-center height-25"
          disabled={!merge.canPreview}
          onClick={merge.previewMerge}
        >
          {merge.operation === "preview" ? "Loading Preview…" : "Preview Case Merge"}
        </button>
      </div>
      <div
        className="case-merge-search"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setShowResults(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            searchInput.current?.focus();
            setShowResults(false);
          }
        }}
      >
        <label htmlFor="case-merge-client-search">Search Client</label>
        <input
          ref={searchInput}
          id="case-merge-client-search"
          name="case-merge-client-search"
          type="search"
          autoComplete="off"
          className="form-control height-25 p-l-5"
          placeholder="Enter first, middle, or last name…"
          value={searchTerm}
          disabled={busy}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              event.currentTarget.parentElement.querySelector(".case-merge-search-result")?.focus();
            }
          }}
        />
        {showResults && searchTerm.trim() && (
          <div
            className="case-merge-search-results"
            role="region"
            aria-label="Client search results"
            aria-busy={clients.loading}
          >
            {clients.loading ? (
              <p role="status">Searching clients…</p>
            ) : clients.error ? (
              <p role="alert">{clients.error}</p>
            ) : clients.data.length === 0 ? (
              <p role="status">No clients found.</p>
            ) : (
              clients.data.map((client) => (
                <button
                  className="case-merge-search-result"
                  type="button"
                  key={client.id}
                  onClick={() => chooseClient(client)}
                >
                  <img src={client.profile_pic_19p || avatarImage} width="15" height="15" alt="" />
                  <span>
                    <strong>
                      {client.last_name},{" "}
                      {[client.first_name, client.middle_name].filter(Boolean).join(" ")}
                    </strong>
                    <span className="case-merge-search-count text-primary-25">
                      {client.case_count}{" "}
                      {client.case_count === 1 ? "case · Nothing to merge" : "cases"}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {selectedClient && (
        <p className="case-merge-selected-client">
          <span className="text-primary-25 p-r-5">Selected client:</span>{" "}
          <strong>
            {selectedClient.last_name}, {selectedClient.first_name}
          </strong>
        </p>
      )}
      {merge.error && (
        <div className="case-merge-notice case-merge-notice-danger" role="alert">
          {merge.error}
          {selectedClient && !merge.data && (
            <button
              type="button"
              className="btn btn-link case-merge-reload"
              disabled={busy || merge.loading}
              onClick={merge.retry}
            >
              Reload Client
            </button>
          )}
        </div>
      )}
      {merge.loading ? (
        <p className="case-merge-empty" role="status">
          Loading client and cases…
        </p>
      ) : !merge.data ? (
        <p className="case-merge-empty">Search for a client to load their details and cases.</p>
      ) : (
        <>
          {cases.length < 2 && (
            <div className="case-merge-notice" role="status">
              {cases.length === 1
                ? "This client has only one case. There is nothing to merge."
                : "This client has no cases to merge."}
            </div>
          )}
          <div className="case-merge-columns">
            <section
              className="case-merge-column background-div"
              aria-labelledby="case-merge-client-heading"
            >
              <h3 id="case-merge-client-heading">Client</h3>
              <ClientDetails client={merge.data.client} />
            </section>
            <section
              className="case-merge-column background-div"
              aria-labelledby="case-merge-final-heading"
            >
              <h3 id="case-merge-final-heading">Final Merged Case</h3>
              <p className="case-merge-column-hint text-primary-25">Select one case to keep.</p>
              <div role="radiogroup" aria-labelledby="case-merge-final-heading">
                {cases.map((caseData) => (
                  <CaseOption
                    key={caseData.id}
                    caseData={caseData}
                    birthday={merge.data.client.birthday}
                    type="radio"
                    checked={merge.targetId === caseData.id}
                    onChange={() => merge.selectTarget(caseData.id)}
                    disabled={busy}
                  />
                ))}
              </div>
            </section>
            <section
              className="case-merge-column background-div"
              aria-labelledby="case-merge-delete-heading"
            >
              <h3 id="case-merge-delete-heading">Merge and Delete Checked Cases</h3>
              <p className="case-merge-column-hint text-primary-25">
                {merge.sourceIds.length} selected. Unchecked cases are left alone.
              </p>
              <div>
                {cases
                  .filter((caseData) => caseData.id !== merge.targetId)
                  .map((caseData) => (
                    <CaseOption
                      key={caseData.id}
                      caseData={caseData}
                      birthday={merge.data.client.birthday}
                      type="checkbox"
                      checked={merge.sourceIds.includes(caseData.id)}
                      onChange={() => merge.toggleSource(caseData.id)}
                      disabled={busy}
                    />
                  ))}
              </div>
            </section>
          </div>
          <p className="case-merge-permanent">
            Merging permanently deletes the checked cases and cannot be undone.
          </p>
        </>
      )}
      <MergePreview
        confirmation={merge.confirmation}
        merging={merge.operation === "merge"}
        onCancel={merge.closeConfirmation}
        onMerge={merge.mergeCases}
      />
    </div>
  );
};

export default CaseMerge;
